/**
 * Fantasy Console 0 - Shared Type Definitions
 */

// SFX types and constants
export type { SFXNote, SFX } from './sfx'
export {
  SFX_COUNT,
  NOTES_PER_SFX,
  SFX_CHANNELS,
  BYTES_PER_NOTE,
  SFX_HEADER_BYTES,
  BYTES_PER_SFX,
  SFX_BANK_BYTES,
  WAVEFORM,
  EFFECT,
} from './sfx'

// Loop guard
import { instrumentLoops as _instrumentLoops } from './loopGuard'
export {
  instrumentLoops,
  LOOP_GUARD_BUDGET,
  LOOP_GUARD_FN_NAME,
  LOOP_GUARD_RESET_FN_NAME,
} from './loopGuard'

/**
 * Cart metadata containing information about the game
 */
export interface CartMetadata {
  title: string
  author: string
  description: string
  version?: string
}

/**
 * Sprite sheet specifications:
 * - 128x128 pixel sheet
 * - 16x16 grid = 256 sprites (0-255)
 * - Each sprite is 8x8 pixels
 * - Each pixel is a color index (0-15)
 * - Total: 16,384 bytes (128 * 128)
 */
export const SPRITE_SHEET_SIZE = 128
export const SPRITE_SIZE = 8
export const SPRITES_PER_ROW = 16
export const TOTAL_SPRITES = 256
export const SPRITE_SHEET_BYTES = SPRITE_SHEET_SIZE * SPRITE_SHEET_SIZE

/**
 * Map specifications:
 * - 128 tiles wide × 64 tiles tall
 * - Each tile is 8×8 pixels (same as sprite size)
 * - Each tile stores a sprite number (0-255)
 * - Total: 8,192 bytes
 */
export const MAP_WIDTH = 128
export const MAP_HEIGHT = 64
export const MAP_BYTES = MAP_WIDTH * MAP_HEIGHT

/**
 * Helper to create an empty sprite sheet
 */
export function createEmptySpriteSheet(): Uint8Array {
  return new Uint8Array(SPRITE_SHEET_BYTES)
}

/**
 * Helper to create an empty map
 */
export function createEmptyMap(): Uint8Array {
  return new Uint8Array(MAP_BYTES)
}

/**
 * Color index type (0-15 for PICO-8 palette)
 */
export type ColorIndex =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15

/**
 * Console configuration
 */
export interface ConsoleConfig {
  /** Screen width in pixels */
  width: number
  /** Screen height in pixels */
  height: number
  /** Target frames per second */
  fps: number
}

/**
 * Default console configuration
 */
export const DEFAULT_CONFIG: ConsoleConfig = {
  width: 128,
  height: 128,
  fps: 60,
}

/**
 * Game lifecycle functions that can be defined in cart code
 */
export interface GameFunctions {
  _init?: () => void
  _update?: () => void
  _draw?: () => void
}

/**
 * Global API functions available in cart code
 * These are exposed on the window object by the Console runtime
 */
export interface GlobalAPI {
  // Drawing functions
  cls: (col?: number) => void
  pset: (x: number, y: number, col?: number) => void
  pget: (x: number, y: number) => number
  rect: (x: number, y: number, w: number, h: number, col?: number) => void
  rectfill: (x: number, y: number, w: number, h: number, col?: number) => void
  circ: (cx: number, cy: number, r: number, col?: number) => void
  circfill: (cx: number, cy: number, r: number, col?: number) => void
  oval: (x: number, y: number, w: number, h: number, col?: number) => void
  ovalfill: (x: number, y: number, w: number, h: number, col?: number) => void
  line: (x0: number, y0: number, x1: number, y1: number, col?: number) => void
  print: (str: string, x?: number, y?: number, col?: number) => void
  printXL: (str: string, x?: number, y?: number, col?: number) => void
  color: (col: number) => void

  // Input functions
  btn: (buttonId: number) => boolean
  btnp: (buttonId: number) => boolean

  // Sprite functions
  spr: (
    n: number,
    x: number,
    y: number,
    w?: number,
    h?: number,
    flipX?: boolean,
    flipY?: boolean,
  ) => void
  sspr: (
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    dw?: number,
    dh?: number,
    flipX?: boolean,
    flipY?: boolean,
  ) => void

  // Palette functions
  pal: (c0?: number, c1?: number, p?: number) => void
  palt: (col?: number, t?: boolean) => void

  // Camera and clipping
  camera: (x?: number, y?: number) => void
  clip: (x?: number, y?: number, w?: number, h?: number) => void

  // Map functions
  map: (
    celX: number,
    celY: number,
    sx: number,
    sy: number,
    celW: number,
    celH: number,
    layer?: number,
  ) => void
  mget: (x: number, y: number) => number
  mset: (x: number, y: number, val: number) => void

  // Math functions
  abs: (x: number) => number
  sgn: (x: number) => number
  min: (x: number, y: number) => number
  max: (x: number, y: number) => number
  mid: (x: number, y: number, z: number) => number
  flr: (x: number) => number
  ceil: (x: number) => number
  sqrt: (x: number) => number
  sin: (x: number) => number
  cos: (x: number) => number
  atan2: (dx: number, dy: number) => number

  // Random functions
  rnd: (x?: number) => number
  srand: (seed: number) => void

  // String functions
  sub: (str: string, pos: number, len?: number) => string
  chr: (val: number) => string
  ord: (str: string, pos?: number) => number | undefined

  // Array functions
  add: <T>(arr: T[], val: T, index?: number) => T
  del: <T>(arr: T[], val: T) => T | undefined
  count: (arr: unknown[] | Record<string, unknown>) => number
  all: <T>(arr: T[]) => T[]
  foreach: <T>(arr: T[], fn: (item: T) => void) => void

  // Audio functions
  sfx: (n: number, channel?: number, offset?: number) => void

  // Debug functions
  printh: (str: string) => void
}

/**
 * Button IDs for input functions
 */
export const BUTTON = {
  LEFT: 0,
  RIGHT: 1,
  UP: 2,
  DOWN: 3,
  A: 4, // Z key
  B: 5, // X key
  START: 6, // Enter key
  SELECT: 7, // Tab key
} as const

export type ButtonId = (typeof BUTTON)[keyof typeof BUTTON]

/**
 * A single code file in a multi-file cart
 */
export interface CodeFile {
  /** File name (e.g., "main.js", "player.js") */
  name: string
  /** The actual code content */
  content: string
}

/**
 * Cart type used by the editor for local state and Supabase storage
 * Parsed when used in the console
 */
export interface Cart {
  /** UUID from Supabase (undefined for unsaved carts) */
  id?: string
  /** Cart owner id */
  userId?: string
  /** Cart metadata */
  metadata: CartMetadata
  /** Cart version */
  version?: number
  /** Array of code files (up to 8 files) */
  codeFiles: CodeFile[]
  /** Sprite sheet data as base64 string (16,384 bytes) */
  sprites: string | null
  /** Map data as base64 string (8,192 bytes) */
  map: string | null
  /** SFX data as base64 string (8,384 bytes), null for carts without sound */
  sfx: string | null
  /** Thumbnail URL from Supabase storage */
  thumbnailUrl?: string
  /** ISO timestamp from Supabase */
  createdAt?: string
  /** ISO timestamp from Supabase */
  updatedAt?: string
  /** Cart id of original cart that this cart was forked from */
  forkedFrom?: string | null
}

/**
 * Default code for new carts
 */
export const DEFAULT_CODE = `function _init() {
  // initialization
}

function _update() {
  // update game state
}

function _draw() {
  cls()
  // draw game
}`

/**
 * Create a new empty cart with optional metadata
 */
export function createEmptyCart(metadata?: Partial<CartMetadata>): Cart {
  return {
    version: 1,
    metadata: {
      title: metadata?.title || 'Untitled Cart',
      author: metadata?.author || '',
      description: metadata?.description || '',
    },
    codeFiles: [
      {
        name: 'main.js',
        content: DEFAULT_CODE,
      },
    ],
    sprites: null,
    map: null,
    sfx: null,
  }
}

/**
 * Prepare code for execution by concatenating all files and instrumenting loops
 */
export function prepareCodeForExecution(codeFiles: CodeFile[]): string {
  const combined = codeFiles
    .map((file) => `// --- ${file.name} ---\n${file.content}`)
    .join('\n\n')
  return _instrumentLoops(combined)
}

// ============================================================
// MESSAGE PASSING TYPES
// ============================================================

/**
 * Messages sent from parent to console iframe
 */
export type ParentToConsoleMessage =
  | { type: 'LOAD_CART'; payload: { cart: Cart } }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'RESET' }
  | { type: 'STEP' }
  | { type: 'CAPTURE_CANVAS' }
  | { type: 'SET_MUTED'; payload: { muted: boolean } }
  | { type: 'UPDATE_SPRITES'; payload: { sprites: string } }
  | { type: 'UPDATE_MAP'; payload: { map: string } }
  | { type: 'UPDATE_SFX'; payload: { sfx: string } }
  | { type: 'START_BOOT' }

/**
 * Messages sent from console iframe to parent
 */
export type ConsoleToParentMessage =
  | { type: 'READY' }
  | { type: 'ERROR'; payload: ErrorPayload }
  | { type: 'LOG'; payload: LogPayload }
  | { type: 'CART_LOADED'; payload: { title: string } }
  | { type: 'CANVAS_CAPTURED'; payload: { dataUrl: string } }
  | { type: 'HEARTBEAT' }
  | { type: 'BOOT_COMPLETE' }

/**
 * Error payload for ERROR messages
 */
export interface ErrorPayload {
  message: string
  stack?: string
  line?: number
  column?: number
}

/**
 * Log payload for LOG messages
 */
export interface LogPayload {
  level: 'log' | 'warn' | 'error'
  message: string
}

// ============================================================
// CART SERIALIZATION UTILITIES
// ============================================================

/**
 * Encode a Uint8Array to a base64 string
 */
export function encodeBase64(data: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i])
  }
  return btoa(binary)
}

/**
 * Decode a base64 string to a Uint8Array
 */
export function decodeBase64(base64: string): Uint8Array {
  const binary = atob(base64)
  const data = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    data[i] = binary.charCodeAt(i)
  }
  return data
}

/**
 * Validation error with helpful message
 */
export class CartValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CartValidationError'
  }
}

/**
 * Validate a cart
 * @throws CartValidationError if validation fails
 */
export function validateCart(cart: unknown): cart is Cart {
  if (!cart || typeof cart !== 'object') {
    throw new CartValidationError('Cart must be an object')
  }

  const c = cart as Record<string, unknown>

  // Check version
  if (c.version && c.version !== 1) {
    throw new CartValidationError('Cart version must be 1')
  }

  // Check metadata
  if (!c.metadata || typeof c.metadata !== 'object') {
    throw new CartValidationError('Cart must have metadata object')
  }
  const meta = c.metadata as Record<string, unknown>
  if (typeof meta.title !== 'string' || meta.title.length === 0) {
    throw new CartValidationError('Cart metadata must have a non-empty title')
  }
  if (typeof meta.author !== 'string') {
    throw new CartValidationError('Cart metadata must have an author string')
  }
  if (typeof meta.description !== 'string') {
    throw new CartValidationError(
      'Cart metadata must have a description string',
    )
  }

  // Check sprites (base64 string or null)
  if (c.sprites !== null) {
    if (typeof c.sprites !== 'string') {
      throw new CartValidationError(
        'Cart sprites must be a base64 string or null',
      )
    }
    const spriteArr = decodeBase64(c.sprites)
    try {
      if (spriteArr.length !== SPRITE_SHEET_BYTES) {
        throw new CartValidationError(
          `Sprite data must be ${SPRITE_SHEET_BYTES} in length, got ${spriteArr.length}`,
        )
      }
      // Validate sprite pixel values (0-15)
      for (let i = 0; i < c.sprites.length; i++) {
        if (spriteArr[i] > 15) {
          throw new CartValidationError(
            `Invalid sprite pixel value ${c.sprites[i]} at index ${i} (must be 0-15)`,
          )
        }
      }
    } catch (e) {
      if (e instanceof CartValidationError) throw e
      throw new CartValidationError('Invalid encoded value for sprites')
    }
  }

  // Check map (base64 string or null)
  if (c.map !== null) {
    if (typeof c.map !== 'string') {
      throw new CartValidationError('Cart map must be a base64 string or null')
    }
    const mapData = decodeBase64(c.map)
    try {
      if (mapData.length !== MAP_BYTES) {
        throw new CartValidationError(
          `Map data must be ${MAP_BYTES} in length, got ${mapData.length}`,
        )
      }
    } catch (e) {
      if (e instanceof CartValidationError) throw e
      throw new CartValidationError('Invalid encoded value for map')
    }
  }

  // TODO: Validate code files

  return true
}

/**
 * Serialize a Cart object to a JSON string
 */
export function serializeCart(cart: Cart): string {
  return JSON.stringify(cart, null, 2)
}

/**
 * Deserialize a JSON string to a Cart object
 * @throws CartValidationError if the JSON is invalid
 */
export function deserializeCart(json: string): Cart {
  let cartData: unknown
  try {
    cartData = JSON.parse(json)
  } catch {
    throw new CartValidationError('Invalid Cart JSON')
  }

  validateCart(cartData)
  return cartData as Cart
}
